/**
 * Copiloto Ejecutivo (Jev) · panel lateral que entiende la vista actual y
 * responde SOLO con las cifras y la evidencia reales de la plataforma.
 * Clasificación determinista en backend (sin texto generado).
 */
import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Sparkles, X, Send, ArrowRight, MapPin, Loader2, AlertTriangle, Info,
} from "lucide-react";
import { API } from "@/context/AuthContext";
import { useZona } from "@/context/ZonaContext";

const NIVEL = {
  alto: "bg-red-50 text-red-700 border-red-200",
  medio: "bg-amber-50 text-amber-700 border-amber-200",
  bajo: "bg-gray-50 text-gray-600 border-gray-200",
};

function fmtMM(v) {
  if (v === null || v === undefined) return "N/D";
  return `${Number(v).toLocaleString("es-ES", { maximumFractionDigits: 0 })} M€`;
}

const MetricCard = ({ it, onGo }) => (
  <button
    onClick={() => it.destino && onGo(it.destino)}
    data-testid={`copilot-metric-${it.clave}`}
    className="text-left bg-white rounded-xl border border-gray-200 p-3 hover:border-[#007934] transition-colors group"
  >
    <p className="text-[10px] text-gray-400 uppercase tracking-wide flex items-center gap-1">
      {it.label}
      {it.info && <Info size={10} className="text-gray-300" title={it.info.formula} />}
    </p>
    {it.valor_pct !== undefined && it.valor_pct !== null ? (
      <p className="text-lg font-bold text-[#007934]">{it.valor_pct}% <span className="text-[11px] font-normal text-gray-400">N={it.valor_n}</span></p>
    ) : it.valor_pct === null && it.clave === "hit_rate" ? (
      <p className="text-lg font-bold text-gray-400">N/D</p>
    ) : (
      <p className="text-lg font-bold text-gray-900">{fmtMM(it.valor_mm)}{it.valor_n != null && <span className="text-[11px] font-normal text-gray-400"> · {it.valor_n}</span>}</p>
    )}
    {it.destino && <span className="text-[10px] text-gray-400 group-hover:text-[#007934] flex items-center gap-0.5">Ver detalle <ArrowRight size={9} /></span>}
  </button>
);

const Answer = ({ ans, onGo }) => (
  <div data-testid="copilot-answer" className="space-y-3">
    {ans.titular && (
      <p className="text-xs text-gray-700 bg-[#F0FDF4] border border-[#007934]/20 rounded-lg p-2.5 leading-relaxed" data-testid="copilot-titular">{ans.titular}</p>
    )}
    <span className="inline-block text-[10px] font-semibold text-[#007934] bg-[#007934]/10 rounded-full px-2 py-0.5">{ans.intent_label}</span>
    {ans.ambito && (
      <p className="text-[10px] text-gray-400" data-testid="copilot-ambito">
        Ámbito: {ans.ambito.year || "todos los años"}
        {ans.ambito.dg ? ` · ${ans.ambito.dg === "SERVICIOS" ? "Urban Services" : "Waste Treatment"}` : " · ambas DG"}
        {ans.ambito.zona ? ` · ${ans.ambito.zona}` : " · todas las zonas"} · corte {ans.corte}
      </p>
    )}

    {ans.bloques.map((b, bi) => (
      <div key={bi} className="space-y-1.5">
        <p className="text-[11px] font-bold text-gray-700">{b.titulo}</p>

        {b.tipo === "metrics" && (
          <div className="grid grid-cols-2 gap-2">
            {b.items.map((it) => <MetricCard key={it.clave} it={it} onGo={onGo} />)}
          </div>
        )}

        {b.tipo === "decisiones" && (
          b.items.length === 0 ? <p className="text-xs text-gray-400 italic">Ninguna pendiente.</p> :
          <div className="space-y-1.5">
            {b.items.map((d, i) => (
              <button key={d.id || i} onClick={() => onGo(`/ofertas-comercial?tab=list&open=${d.id}`)} data-testid={`copilot-decision-${i}`}
                className="w-full text-left bg-white rounded-lg border border-gray-200 p-2.5 hover:border-[#007934] transition-colors">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-gray-900 truncate">{d.nombre || d.objeto}</p>
                  <span className="text-xs font-bold text-[#007934] whitespace-nowrap">{fmtMM(d.importe_mm)}</span>
                </div>
                {d.objeto && d.nombre && <p className="text-[10px] text-gray-400 truncate">{d.objeto}</p>}
                <div className="flex items-center gap-1.5 mt-0.5">
                  {d.zona && <span className="text-[9px] text-gray-400">{d.zona}</span>}
                  {d.dg && <span className="text-[9px] text-gray-400">· {d.dg}</span>}
                </div>
              </button>
            ))}
          </div>
        )}

        {b.tipo === "zonas" && (
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="w-full text-[11px]">
              <thead className="bg-gray-50 text-gray-400">
                <tr><th className="text-left px-2 py-1 font-medium">Zona</th><th className="text-right px-2 py-1 font-medium">Valor</th><th className="text-right px-2 py-1 font-medium">Acierto</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {b.items.map((z) => (
                  <tr key={z.codigo} data-testid={`copilot-zona-${z.codigo}`} className="hover:bg-gray-50">
                    <td className="px-2 py-1.5">
                      <span className="font-semibold text-gray-800">{z.nombre_visible}</span>
                      {z.responsable && <span className="block text-[9px] text-gray-400">{z.responsable}</span>}
                    </td>
                    <td className="px-2 py-1.5 text-right text-gray-700">{fmtMM(z.valor_mm)}<span className="block text-[9px] text-gray-400">{z.operaciones} ops</span></td>
                    <td className="px-2 py-1.5 text-right font-semibold text-[#007934]">{z.hit_rate_value != null ? `${z.hit_rate_value}%` : "N/D"}<span className="block text-[9px] text-gray-400 font-normal">N={z.hit_rate_n}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {b.tipo === "riesgos" && (
          b.items.length === 0 ? <p className="text-xs text-gray-400 italic">Sin riesgos destacados.</p> :
          <div className="space-y-1.5">
            {b.items.map((r, i) => (
              <button key={r.clave || i} onClick={() => r.destino && onGo(r.destino)} data-testid={`copilot-riesgo-${i}`}
                className={`w-full text-left rounded-lg border p-2.5 hover:opacity-90 transition ${NIVEL[r.nivel] || NIVEL.bajo}`}>
                <div className="flex items-center gap-1.5">
                  <AlertTriangle size={12} />
                  <p className="text-xs font-semibold">{r.titulo}</p>
                </div>
                <p className="text-[10px] opacity-80 mt-0.5">{r.detalle}</p>
              </button>
            ))}
          </div>
        )}
      </div>
    ))}

    {ans.nota && (
      <p className="text-[11px] text-gray-500 flex items-start gap-1" data-testid="copilot-nota"><Info size={12} className="mt-0.5 flex-shrink-0" />{ans.nota}</p>
    )}
    {ans.evidencia?.length > 0 && (
      <div className="flex flex-wrap gap-1.5 pt-1">
        {ans.evidencia.map((e, i) => (
          <button key={i} onClick={() => onGo(e.destino)} data-testid={`copilot-evidence-${i}`}
            className="text-[11px] font-medium text-[#007934] border border-[#007934]/30 rounded-full px-2 py-0.5 hover:bg-[#007934]/10 flex items-center gap-1">
            {e.label} <ArrowRight size={10} />
          </button>
        ))}
      </div>
    )}
    <p className="text-[10px] text-gray-400 pt-1 border-t border-gray-100">{ans.disclaimer}</p>
  </div>
);

const SUGS = [
  "¿Cuánto llevamos presentado y adjudicado?",
  "¿Qué decisiones hay pendientes?",
  "Compara el acierto por zona",
  "¿Qué riesgos hay ahora mismo?",
];

export function CopilotPanel() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [ans, setAns] = useState(null);
  const [err, setErr] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const { zonaGlobal, yearGlobal, dgGlobal } = useZona();
  const scrollRef = useRef(null);

  const ask = async (question) => {
    const text = (question ?? q).trim();
    if (!text) return;
    setLoading(true); setErr(""); setAns(null);
    try {
      const { data } = await axios.post(`${API}/copilot/ask`, {
        question: text,
        context: {
          view: location.pathname,
          zona: zonaGlobal || null,
          year: yearGlobal ? Number(yearGlobal) : null,
          dg: dgGlobal || null,
        },
      });
      setAns(data);
    } catch (e) {
      setErr("No se pudo responder. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (ans && scrollRef.current) scrollRef.current.scrollTop = 0; }, [ans]);

  const goTo = (destino) => { setOpen(false); navigate(destino); };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="copilot-fab"
          title="Copiloto Ejecutivo"
          className="fixed bottom-5 right-5 z-40 flex items-center gap-2 bg-[#007934] text-white rounded-full shadow-lg shadow-[#007934]/30 pl-3 pr-4 py-3 hover:bg-[#065f30] transition-all hover:scale-105 print:hidden">
          <Sparkles size={18} />
          <span className="text-sm font-semibold hidden sm:inline">Copiloto</span>
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex justify-end print:hidden" data-testid="copilot-overlay">
          <div className="absolute inset-0 bg-black/20" onClick={() => setOpen(false)} />
          <aside data-testid="copilot-panel" className="relative w-full sm:w-[420px] max-w-full bg-gray-50 h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
            <header className="bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#007934]/10 flex items-center justify-center">
                  <Sparkles size={16} className="text-[#007934]" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900 leading-tight">Copiloto Ejecutivo</p>
                  <p className="text-[10px] text-gray-400 flex items-center gap-1 flex-wrap">
                    {zonaGlobal ? <><MapPin size={9} /> {zonaGlobal}</> : "Todas las zonas"}
                    {yearGlobal && <> · {yearGlobal}</>}
                    {dgGlobal && <> · {dgGlobal === "SERVICIOS" ? "Urban Services" : "Waste Treatment"}</>}
                    {" "}· cifras exactas
                  </p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="copilot-close" className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </header>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {!ans && !loading && (
                <div className="space-y-3">
                  <p className="text-xs text-gray-500">Pregunta sobre el negocio. Respondo solo con las cifras y la evidencia reales de la plataforma —sin estimaciones.</p>
                  <div className="space-y-1.5">
                    {SUGS.map((s, i) => (
                      <button key={i} onClick={() => { setQ(s); ask(s); }} data-testid={`copilot-suggestion-${i}`}
                        className="w-full text-left text-xs text-gray-700 bg-white border border-gray-200 rounded-lg px-3 py-2 hover:border-[#007934] hover:text-[#007934] transition-colors flex items-center justify-between gap-2">
                        {s} <ArrowRight size={12} className="text-gray-300 flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {loading && <div className="flex items-center gap-2 text-sm text-gray-400 py-6"><Loader2 className="animate-spin" size={16} /> Calculando…</div>}
              {err && <p className="text-xs text-red-600" data-testid="copilot-error">{err}</p>}
              {ans && <Answer ans={ans} onGo={goTo} />}
            </div>

            <div className="border-t border-gray-100 bg-white p-3">
              <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex items-center gap-2">
                <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="copilot-input"
                  placeholder="Escribe tu pregunta…" maxLength={500}
                  className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:border-[#007934] focus:outline-none" />
                <button type="submit" disabled={loading || !q.trim()} data-testid="copilot-send"
                  className="bg-[#007934] text-white rounded-lg p-2 hover:bg-[#065f30] disabled:opacity-40">
                  <Send size={16} />
                </button>
              </form>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
